import { useState, useRef, useEffect, useMemo } from "react";
import { Eye } from "lucide-react";
import { navigate } from "wouter/use-browser-location";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useQuery } from "@tanstack/react-query";
import { fetchCallHistory } from "../api/callHistory";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctors } from "../redux/doctors-actions";
import DoctorMultiSelect from "../components/DoctorMultiSelect";
import { usePermission, usePermissionLevel } from "../hooks/use-permission";
import { useTour } from "../components/tour/TourProvider";

const norm = (str) =>
  (str || "").toString().toLowerCase().trim().replace(/\s+/g, "");

const dateOnly = (iso) => {
  const d = new Date(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

const getCallOwner = (entry = {}) =>
  pickFirst(
    entry.userID,
    entry.userId,
    entry.user_id,
    entry.doctor_email,
    entry.doctorEmail,
    entry.email,
    entry.details?.userID,
    entry.details?.doctor_email,
    entry.original_json?.userID,
    entry.original_json?.doctor_email
  );

const getCallAppointmentId = (entry = {}) =>
  pickFirst(
    entry.appointmentID,
    entry.appointmentId,
    entry.appointment_id,
    entry.callId,
    entry.details?.appointmentID,
    entry.details?.appointmentId,
    entry.original_json?.appointmentID,
    entry.original_json?.appointmentId
  );

const getCallPatientName = (entry = {}) =>
  pickFirst(
    entry.patientName,
    entry.patient_name,
    entry.full_name,
    entry.patient?.full_name,
    entry.patient?.name,
    entry.details?.patientName,
    entry.details?.patient_name,
    entry.original_json?.patientName,
    entry.original_json?.patient_name
  );

const getCallDoctorName = (entry = {}, owner = "") =>
  pickFirst(
    entry.fullName,
    entry.doctorName,
    entry.doctor_name,
    entry.providerName,
    entry.provider_name,
    entry.details?.fullName,
    entry.details?.doctorName,
    entry.original_json?.fullName,
    entry.original_json?.doctorName
  );

const getCallStartTime = (entry = {}) =>
  pickFirst(
    entry.startTime,
    entry.start_time,
    entry.startedAt,
    entry.started_at,
    entry.created_at,
    entry.createdAt,
    entry.details?.startTime,
    entry.original_json?.startTime
  );

const getCallEndTime = (entry = {}) =>
  pickFirst(
    entry.endTime,
    entry.end_time,
    entry.endedAt,
    entry.ended_at,
    entry.updated_at,
    entry.updatedAt,
    entry.details?.endTime,
    entry.original_json?.endTime
  );

const isValidDateValue = (value) => {
  if (!value) return false;
  return !Number.isNaN(new Date(value).getTime());
};

const formatDateValue = (value) => {
  if (!isValidDateValue(value)) return "N/A";
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatTimeValue = (value) =>
  isValidDateValue(value)
    ? new Date(value).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";

const normalizeCallHistoryEntry = (entry = {}) => {
  const userID = String(getCallOwner(entry) || "").trim().toLowerCase();
  const appointmentID = getCallAppointmentId(entry);
  const startTime = getCallStartTime(entry);
  const endTime = getCallEndTime(entry);
  const patientName = getCallPatientName(entry);

  if (!userID || !patientName || !isValidDateValue(startTime)) {
    return null;
  }

  return {
    ...entry,
    id:
      entry.id ||
      entry._id ||
      [userID, appointmentID, startTime, patientName].filter(Boolean).join("-"),
    userID,
    appointmentID,
    patientName,
    fullName: getCallDoctorName(entry, userID) || "N/A",
    startTime,
    endTime,
  };
};

const cleanName = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length > 1 && parts.length % 2 === 0) {
    const half = parts.length / 2;
    const firstHalf = parts.slice(0, half).join(' ');
    const secondHalf = parts.slice(half).join(' ');
    if (firstHalf.toLowerCase() === secondHalf.toLowerCase()) {
      return firstHalf; // Return the non-duplicated part (e.g., "John Doe" from "John Doe John Doe")
    }
  }
  return name;
};

const CallHistoryCard = ({ entry, canViewPostCall, tourActive = false }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-4 flex items-center gap-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6 text-sm text-gray-900 flex-1">
      <div>
        <span className="font-semibold">Patient Name:</span>
        <span className="ml-2">{entry.patientName}</span>
      </div>

      <div>
        <span className="font-semibold">Dr Name:</span>
        <span className="ml-2">{cleanName(entry.fullName)}</span>
      </div>

      <div>
        <span className="font-semibold">Date:</span>
        <span className="ml-2">{formatDateValue(entry.startTime)}</span>
      </div>

      <div>
        <span className="font-semibold">Start Time:</span>
        <span className="ml-2">{formatTimeValue(entry.startTime)}</span>
      </div>

      <div>
        <span className="font-semibold">End Time:</span>
        <span className="ml-2">{formatTimeValue(entry.endTime)}</span>
      </div>
    </div>

    {canViewPostCall && entry.appointmentID ? (
      <button
        onClick={() =>
          navigate(`/post-call/${entry.appointmentID}?username=${entry.userID}`, {
            state: {
              startTime: entry.startTime,
              endTime: entry.endTime,
            },
          })
        }
        data-tour="history-soap-btn"
        title="View Practice Intelligence"
        className="text-blue-600 hover:text-blue-800 ml-auto"
      >
        <Eye className="w-5 h-5" />
      </button>
    ) : tourActive ? (
      <span
        data-tour="history-soap-btn"
        title="Practice Intelligence appears here when access is available"
        className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-md border border-blue-100 bg-blue-50 text-blue-500"
      >
        <Eye className="w-5 h-5" />
      </span>
    ) : null}
  </div>
);

function CallHistory({ canViewAllProviderCalls: canViewAllProviderCallsProp }) {
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const dispatch = useDispatch();
  const doctors = useSelector((state) => state.doctors?.doctors || []);

  const doctorEmail = useSelector(
    (state) => state.me.me.email?.toLowerCase()
  );

  const clinicName = useSelector(
    (state) => state.me.me.clinicName
  );
  const canViewPostCall = usePermission("post_call.view_all", "read");
  const { tour } = useTour();
  const callHistoryPermissionLevel = usePermissionLevel("video_call.history");
  const canViewCallHistory = ["read", "write"].includes(callHistoryPermissionLevel);
  const canViewAllProviderCallsFallback = callHistoryPermissionLevel === "write";
  const canViewAllProviderCalls =
    canViewAllProviderCallsProp === undefined
      ? canViewAllProviderCallsFallback
      : Boolean(canViewAllProviderCallsProp);
  const effectiveSelectedDoctors = useMemo(() => {
    if (!doctorEmail) {
      return [];
    }

    if (!canViewAllProviderCalls) {
      return [doctorEmail];
    }

    return selectedDoctors.length > 0 ? selectedDoctors : [doctorEmail];
  }, [canViewAllProviderCalls, doctorEmail, selectedDoctors]);

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (doctors.length === 0) dispatch(fetchDoctors(clinicName));
  }, [doctors.length, dispatch, clinicName]);

  useEffect(() => {
    if (!doctorEmail) {
      return;
    }

    if (!canViewAllProviderCalls) {
      setSelectedDoctors((current) =>
        current.length === 1 && current[0] === doctorEmail ? current : [doctorEmail]
      );
      setShowDropdown(false);
      return;
    }

    if (selectedDoctors.length === 0) {
      setSelectedDoctors([doctorEmail]);
    }
  }, [canViewAllProviderCalls, doctorEmail, selectedDoctors.length]);

  const { data: callHistoryData = [], isLoading } = useQuery({
    queryKey: ["call-history", effectiveSelectedDoctors],
    queryFn: () => fetchCallHistory(effectiveSelectedDoctors),
    enabled:
      canViewCallHistory &&
      Boolean(doctorEmail) &&
      effectiveSelectedDoctors.length > 0,
  });

  const dataForFiltering = useMemo(() => {
    if (tour?.active && callHistoryData.length === 0) {
      return [
        {
          id: "tour-history-1",
          patientName: "John Doe",
          fullName: "Dr. Local Dev User",
          startTime: new Date(Date.now() - 3600000).toISOString(),
          endTime: new Date().toISOString(),
          appointmentID: "tour-appointment-id",
          userID: doctorEmail || "local.dev@seismic.test",
        },
      ];
    }
    return callHistoryData;
  }, [tour?.active, callHistoryData, doctorEmail]);

  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    let data = (Array.isArray(dataForFiltering) ? dataForFiltering : [])
      .map(normalizeCallHistoryEntry)
      .filter(Boolean);
    const searchValue = norm(patientSearch);
    const selectedDoctorSet = new Set(effectiveSelectedDoctors.map(norm));

    data = data.filter((item) => {
      const providerMatch =
        (tour?.active && selectedDoctorSet.size === 0) ||
        selectedDoctorSet.has(norm(item.userID));

      const hasDateFilter = Boolean(startDate || endDate);
      const hasValidStartDate = isValidDateValue(item.startTime);
      const d = hasValidStartDate ? dateOnly(item.startTime) : null;
      const dateMatch =
        (!hasDateFilter || hasValidStartDate) &&
        (!startDate || d >= dateOnly(startDate)) &&
        (!endDate || d <= dateOnly(endDate));

      const patientMatch =
        !searchValue || norm(item.patientName).includes(searchValue);

      // Use strict normalization (remove all whitespace) for reliable matching
      const userClinic = norm(clinicName);
      const entryClinic = norm(
        item.clinicName ||
        item.details?.clinicName ||
        item.original_json?.clinicName ||
        item.clinic_name ||
        item.details?.clinic_name ||
        item.practiceName
      );

      // If user has a clinic, only show entries that match that clinic or have no clinic data (legacy/missing)
      const clinicMatch = !userClinic || !entryClinic || entryClinic === userClinic;

      return providerMatch && dateMatch && patientMatch && clinicMatch;
    });

    data.sort((a, b) => {
      const bTime = isValidDateValue(b.startTime) ? new Date(b.startTime).getTime() : 0;
      const aTime = isValidDateValue(a.startTime) ? new Date(a.startTime).getTime() : 0;
      return bTime - aTime;
    });
    setFilteredData(data);
  }, [
    dataForFiltering,
    effectiveSelectedDoctors,
    startDate,
    endDate,
    patientSearch,
    clinicName,
    tour?.active,
  ]);

  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setPatientSearch("");
    if (doctorEmail) {
      setSelectedDoctors([doctorEmail.toLowerCase()]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">
          Call History Overview
        </h1>
        <p className="text-sm text-gray-500">
          Review previous consultations filtered by provider, date, and patient.
        </p>
      </div>

      {/* Filters */}
      <div data-tour="history-filters" className="flex flex-wrap items-end gap-4">
        {canViewAllProviderCalls ? (
          <div ref={dropdownRef}>
            <DoctorMultiSelect
              selectedDoctors={selectedDoctors}
              onDoctorSelect={(_, list) =>
                setSelectedDoctors(list.map((d) => d.email.toLowerCase()))
              }
              isDropdownOpen={showDropdown}
              setDropdownOpen={setShowDropdown}
            />
          </div>
        ) : null}

        {/* Date range – future dates disabled */}
        <DatePicker
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={([s, e]) => {
            setStartDate(s);
            setEndDate(e);
          }}
          placeholderText="Select date range"
          className="h-10 border border-gray-300 rounded-md px-4 text-sm w-64"
          isClearable
          maxDate={new Date()}
        />

        <input
          value={patientSearch}
          onChange={(e) => setPatientSearch(e.target.value)}
          placeholder="Patient name"
          className="h-10 border rounded-md px-4 text-sm w-64"
        />

        <button
          onClick={resetFilters}
          className="text-sm text-gray-500 hover:underline"
        >
          Reset Filters
        </button>
      </div>

      {/* Results */}
      <div data-tour="history-list" className="space-y-3">
        {isLoading && (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        )}

        {!isLoading && filteredData.length > 0 ? (
          filteredData.map((entry) => (
            <CallHistoryCard
              key={entry.id}
              entry={entry}
              canViewPostCall={canViewPostCall}
              tourActive={tour?.active}
            />
          ))
        ) : (
          <div className="p-6 text-center text-sm text-gray-500 bg-gray-50 border rounded-lg">
            No past video calls found for this provider and filter.
          </div>
        )}
      </div>


    </div>
  );
}

export default CallHistory
